import { ThemeEvents } from '@theme/events';

const HIDDEN = 'is-hidden';

/**
 * Product/article share control: Web Share API when available, otherwise copy-link fallback.
 * @extends {HTMLElement}
 */
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.mainDetailsToggle = this.querySelector('[data-share-details]');
    this.shareButton = this.querySelector('[data-share-button]');
    this.shareCopy = this.querySelector('[data-share-copy]');
    this.shareSummary = this.querySelector('[data-share-summary]');
    this.closeButton = this.querySelector('[data-close-button]');
    this.successMessage = this.querySelector('[data-success-message]');
    this.urlInput = this.querySelector('[data-share-url]');
    this.urlToShare = this.urlInput ? this.urlInput.value : document.location.href;
    this.copiedLabel = this.dataset.successMessage || 'Link copied to clipboard';

    this.#init();
    this.#bindVariantUpdates();
  }

  disconnectedCallback() {
    this.closest('.shopify-section')?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #init() {
    if (navigator.share) {
      this.mainDetailsToggle?.classList.add(HIDDEN);
      this.shareButton?.classList.remove(HIDDEN);
      this.shareButton?.addEventListener('click', () => {
        navigator.share({
          url: this.urlToShare,
          title: document.title,
        });
      });
      return;
    }

    this.mainDetailsToggle?.addEventListener('toggle', () => this.#toggleDetails());
    this.mainDetailsToggle?.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.#close();
        }
      });
    });
    this.shareCopy?.addEventListener('click', () => this.#copyToClipboard());
    this.closeButton?.addEventListener('click', () => this.#close());
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.mainDetailsToggle?.focus();
        this.#close();
      }
    });
  }

  #bindVariantUpdates() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const base = this.urlToShare.split('?')[0];
    this.urlToShare = `${base}?variant=${variant.id}`;
    if (this.urlInput) {
      this.urlInput.value = this.urlToShare;
    }
  };

  #toggleDetails() {
    if (this.mainDetailsToggle?.open) return;
    this.successMessage?.classList.add(HIDDEN);
    if (this.successMessage) this.successMessage.textContent = '';
    this.closeButton?.classList.add(HIDDEN);
    this.shareCopy?.focus();
  }

  #copyToClipboard() {
    if (!this.urlInput) return;
    navigator.clipboard.writeText(this.urlInput.value).then(() => {
      this.successMessage?.classList.remove(HIDDEN);
      if (this.successMessage) this.successMessage.textContent = this.copiedLabel;
      this.closeButton?.classList.remove(HIDDEN);
      this.closeButton?.focus();
    });
  }

  #close() {
    this.mainDetailsToggle?.removeAttribute('open');
    this.shareSummary?.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButton);
}
